import { isMock, db } from './firebase'
import { 
  doc, 
  getDoc, 
  setDoc,
  serverTimestamp,
  DocumentData
} from 'firebase/firestore'

// Firestore collections
const AI_CONFIG_COLLECTION = 'ai_config'
const AI_CONFIG_DOC = 'openai_settings'

// Tipos
export interface AIConfig {
  apiKey: string
  provider: 'openai' | 'gemini' | 'custom'
  model: string
  enabled: boolean
  maxTokens: number
  temperature: number
  createdAt: Date
  updatedAt: Date
  createdBy: string
}

export interface AISearchResult {
  answer: string
  sources: Array<{
    id: string
    type: 'news' | 'listing' | 'group' | 'user'
    title: string
    excerpt: string
    relevance: number
    url: string
  }>
  suggestedQueries: string[]
}

export interface SearchDocument {
  id: string
  type: 'news' | 'listing' | 'group' | 'user'
  title: string
  content: string
  excerpt: string
  metadata: Record<string, unknown>
  url: string
  relevanceScore?: number
}

// Helper para codificar/decodificar API key (base64 simples)
const encodeApiKey = (key: string): string => {
  return btoa(key)
}

const decodeApiKey = (encoded: string): string => {
  return atob(encoded)
}

// Serviço de configuração de IA
export const aiConfigService = {
  // Salvar configuração de IA
  async saveAIConfig(config: Partial<AIConfig> & { apiKey: string }, userId: string): Promise<void> {
    if (isMock) {
      console.log('Mock: Salvando configuração de IA', { ...config, apiKey: '***' })
      // Salvar no localStorage para simulação
      localStorage.setItem('noe_ai_api_key', encodeApiKey(config.apiKey))
      localStorage.setItem('noe_ai_config', JSON.stringify({
        ...config,
        apiKey: '***',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: userId
      }))
      return
    }

    try {
      const configRef = doc(db, AI_CONFIG_COLLECTION, AI_CONFIG_DOC)
      const now = serverTimestamp()
      
      const configData: DocumentData = {
        apiKey: encodeApiKey(config.apiKey),
        provider: config.provider || 'openai',
        model: config.model || 'gpt-3.5-turbo',
        enabled: config.enabled !== false,
        maxTokens: config.maxTokens || 500,
        temperature: config.temperature || 0.7,
        updatedAt: now,
        createdBy: userId
      }
      
      // Se não existir, adicionar createdAt
      const existing = await getDoc(configRef)
      if (!existing.exists()) {
        configData.createdAt = now
      }
      
      await setDoc(configRef, configData, { merge: true })
      console.log('Configuração de IA salva com sucesso')
    } catch (error) {
      console.error('Erro ao salvar configuração de IA:', error)
      throw error
    }
  },
  
  // Obter configuração de IA (sem API key)
  async getAIConfig(): Promise<AIConfig | null> {
    if (isMock) {
      const stored = localStorage.getItem('noe_ai_config')
      if (stored) {
        const config = JSON.parse(stored)
        return {
          ...config,
          createdAt: new Date(config.createdAt),
          updatedAt: new Date(config.updatedAt)
        }
      }
      return null
    }
    
    try {
      const configRef = doc(db, AI_CONFIG_COLLECTION, AI_CONFIG_DOC)
      const configDoc = await getDoc(configRef)
      
      if (!configDoc.exists()) {
        return null
      }
      
      const data = configDoc.data()
      return {
        apiKey: data.apiKey ? decodeApiKey(data.apiKey) : '',
        provider: data.provider || 'openai',
        model: data.model || 'gpt-3.5-turbo',
        enabled: data.enabled !== false,
        maxTokens: data.maxTokens || 500,
        temperature: data.temperature || 0.7,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        createdBy: data.createdBy || ''
      }
    } catch (error) {
      console.error('Erro ao obter configuração de IA:', error)
      return null
    }
  },
  
  // Verificar se IA está configurada e habilitada
  async isAIAvailable(): Promise<boolean> {
    if (isMock) {
      return localStorage.getItem('noe_ai_api_key') !== null
    }
    
    try {
      const config = await this.getAIConfig()
      return !!config && config.enabled && !!config.apiKey
    } catch (error) {
      console.error('Erro ao verificar disponibilidade de IA:', error)
      return false
    }
  },
  
  // Obter API key (apenas para uso interno)
  async getAPIKey(): Promise<string | null> {
    if (isMock) {
      const encoded = localStorage.getItem('noe_ai_api_key')
      return encoded ? decodeApiKey(encoded) : null
    }
    
    try {
      const config = await this.getAIConfig()
      return config?.apiKey || null
    } catch (error) {
      console.error('Erro ao obter API key:', error)
      return null
    }
  }
}

// Serviço de busca com IA
export const aiSearchService = {
  // Buscar documentos do Firebase com base na query
  async searchDocuments(query: string): Promise<SearchDocument[]> {
    if (isMock) {
      // Dados mock para demonstração
      return [
        {
          id: 'news-1',
          type: 'news' as const,
          title: 'Workshop de Imigração para Brasileiros',
          content: 'Evento gratuito sobre vistos de trabalho e oportunidades nos EUA. Palestras com especialistas em imigração.',
          excerpt: 'Evento gratuito sobre vistos de trabalho',
          metadata: { category: 'Comunidade', date: '2026-04-10' },
          url: '/news/news-1',
          relevanceScore: 0.95
        },
        {
          id: 'listing-1',
          type: 'listing' as const,
          title: 'Apartamento 2 quartos em Miami',
          content: 'Apartamento mobiliado próximo à praia. Aceita pets. Valor: $2,500/mês.',
          excerpt: 'Apartamento mobiliado próximo à praia',
          metadata: { category: 'property', price: 2500, location: 'Miami, FL' },
          url: '/listings/listing-1',
          relevanceScore: 0.85
        },
        {
          id: 'group-1',
          type: 'group' as const,
          title: 'Brasileiros em Orlando',
          content: 'Grupo para brasileiros que moram em Orlando compartilharem dicas e oportunidades.',
          excerpt: 'Grupo para brasileiros em Orlando',
          metadata: { members: 245, category: 'Comunidade' },
          url: '/groups/group-1',
          relevanceScore: 0.75
        }
      ]
    }
    
    try {
      const results: SearchDocument[] = []
      const searchTerms = query.toLowerCase().split(' ').filter(term => term.length > 2)
      
      // Se não houver termos válidos, retornar array vazio
      if (searchTerms.length === 0) {
        return results
      }
      
      // Buscar em múltiplas coleções em paralelo
      const [newsResults, listingResults, groupResults] = await Promise.all([
        this.searchNews(searchTerms),
        this.searchListings(searchTerms),
        this.searchGroups(searchTerms)
      ])
      
      // Combinar resultados
      results.push(...newsResults, ...listingResults, ...groupResults)
      
      // Ordenar por relevância
      results.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0))
      
      return results.slice(0, 20) // Limitar a 20 resultados
    } catch (error) {
      console.error('Erro na busca de documentos:', error)
      return []
    }
  },
  
  // Buscar em notícias
  async searchNews(searchTerms: string[]): Promise<SearchDocument[]> {
    try {
      const { collection, query, where, getDocs, orderBy, limit } = await import('firebase/firestore')
      const newsRef = collection(db, 'news')
      const q = query(newsRef, where('status', '==', 'published'), orderBy('createdAt', 'desc'), limit(50))
      const snapshot = await getDocs(q)
      
      const results: SearchDocument[] = []
      snapshot.forEach(doc => {
        const data = doc.data()
        const title = data.title || ''
        const excerpt = data.excerpt || ''
        const content = data.content || ''
        const combinedText = (title + ' ' + excerpt + ' ' + content).toLowerCase()
        
        // Calcular relevância baseada nos termos de busca
        let relevance = 0
        searchTerms.forEach(term => {
          if (combinedText.includes(term)) {
            relevance += 1
            // Bonus se o termo estiver no título
            if (title.toLowerCase().includes(term)) {
              relevance += 2
            }
          }
        })
        
        if (relevance > 0) {
          results.push({
            id: doc.id,
            type: 'news',
            title: title,
            content: content,
            excerpt: excerpt,
            metadata: {
              category: data.category,
              date: data.date,
              author: data.author,
              views: data.views
            },
            url: `/news/${doc.id}`,
            relevanceScore: relevance / (searchTerms.length * 3) // Normalizar para 0-1
          })
        }
      })
      
      return results
    } catch (error) {
      console.error('Erro na busca de notícias:', error)
      return []
    }
  },
  
  // Buscar em anúncios
  async searchListings(searchTerms: string[]): Promise<SearchDocument[]> {
    try {
      const { collection, query, where, getDocs, orderBy, limit } = await import('firebase/firestore')
      const listingsRef = collection(db, 'listings')
      const q = query(listingsRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'), limit(50))
      const snapshot = await getDocs(q)
      
      const results: SearchDocument[] = []
      snapshot.forEach(doc => {
        const data = doc.data()
        const title = data.title || ''
        const description = data.description || ''
        const location = data.location || ''
        const combinedText = (title + ' ' + description + ' ' + location).toLowerCase()
        
        let relevance = 0
        searchTerms.forEach(term => {
          if (combinedText.includes(term)) {
            relevance += 1
            if (title.toLowerCase().includes(term)) {
              relevance += 2
            }
          }
        })
        
        if (relevance > 0) {
          results.push({
            id: doc.id,
            type: 'listing',
            title: title,
            content: description,
            excerpt: description.substring(0, 150) + '...',
            metadata: {
              category: data.category,
              price: data.price,
              location: location,
              city: data.city,
              state: data.state
            },
            url: `/listings/${doc.id}`,
            relevanceScore: relevance / (searchTerms.length * 3)
          })
        }
      })
      
      return results
    } catch (error) {
      console.error('Erro na busca de anúncios:', error)
      return []
    }
  },
  
  // Buscar em grupos
  async searchGroups(searchTerms: string[]): Promise<SearchDocument[]> {
    try {
      const { collection, query, where, getDocs, orderBy, limit } = await import('firebase/firestore')
      const groupsRef = collection(db, 'groups')
      const q = query(groupsRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'), limit(50))
      const snapshot = await getDocs(q)
      
      const results: SearchDocument[] = []
      snapshot.forEach(doc => {
        const data = doc.data()
        const name = data.name || ''
        const description = data.description || ''
        const combinedText = (name + ' ' + description).toLowerCase()
        
        let relevance = 0
        searchTerms.forEach(term => {
          if (combinedText.includes(term)) {
            relevance += 1
            if (name.toLowerCase().includes(term)) {
              relevance += 2
            }
          }
        })
        
        if (relevance > 0) {
          results.push({
            id: doc.id,
            type: 'group',
            title: name,
            content: description,
            excerpt: description.substring(0, 150) + '...',
            metadata: {
              category: data.category,
              membersCount: data.membersCount,
              privacy: data.privacy
            },
            url: `/groups/${doc.id}`,
            relevanceScore: relevance / (searchTerms.length * 3)
          })
        }
      })
      
      return results
    } catch (error) {
      console.error('Erro na busca de grupos:', error)
      return []
    }
  },
  
  // Executar busca inteligente com IA
  async intelligentSearch(query: string): Promise<AISearchResult> {
    const apiKey = await aiConfigService.getAPIKey()
    
    if (!apiKey) {
      throw new Error('API key de IA não configurada')
    }
    
    // Buscar documentos relevantes do Firebase
    const documents = await this.searchDocuments(query)
    
    // Preparar contexto para IA
    const context = documents.map(doc => 
      `[${doc.type.toUpperCase()}] ${doc.title}: ${doc.excerpt}`
    ).join('\n\n')
    
    // Chamar API da OpenAI (ou outro provider)
    const response = await this.callOpenAI(apiKey, query, context, documents)
    
    return response
  },
  
  // Chamar API da OpenAI
  async callOpenAI(apiKey: string, query: string, context: string, documents: SearchDocument[]): Promise<AISearchResult> {
    // Mock para desenvolvimento
    if (isMock) {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simular delay
      
      return {
        answer: `Com base nas informações disponíveis no portal Noé Connect, encontrei informações relevantes sobre "${query}". Existe um workshop de imigração gratuito para brasileiros e um apartamento disponível em Miami. Recomendo verificar os detalhes nos links abaixo.`,
        sources: documents.map(doc => ({
          id: doc.id,
          type: doc.type,
          title: doc.title,
          excerpt: doc.excerpt,
          relevance: doc.relevanceScore || 0.8,
          url: doc.url
        })),
        suggestedQueries: [
          'Como funciona o visto de trabalho para brasileiros?',
          'Quais são os melhores bairros para brasileiros em Miami?',
          'Como encontrar emprego nos EUA como brasileiro?'
        ]
      }
    }
    
    // Implementação real da OpenAI
    try {
      const config = await aiConfigService.getAIConfig()
      const model = config?.model || 'gpt-3.5-turbo'
      const maxTokens = config?.maxTokens || 500
      const temperature = config?.temperature || 0.7
      
      const prompt = `Você é um assistente especializado no portal Noé Connect, um marketplace para brasileiros nos Estados Unidos.
      
Contexto do portal (dados reais):
${context}

Pergunta do usuário: ${query}

Instruções:
1. Responda de forma útil e amigável, focando nas informações disponíveis no portal
2. Se não encontrar informações relevantes, sugira onde o usuário pode buscar mais informações
3. Inclua referências aos itens do contexto quando relevante
4. Responda em português brasileiro

Resposta:`
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: 'Você é um assistente útil do Noé Connect, um portal para brasileiros nos EUA.' },
            { role: 'user', content: prompt }
          ],
          max_tokens: maxTokens,
          temperature,
          n: 1
        })
      })
      
      if (!response.ok) {
        throw new Error(`Erro na API OpenAI: ${response.statusText}`)
      }
      
      const data = await response.json()
      const answer = data.choices[0].message.content
      
      return {
        answer,
        sources: documents.map(doc => ({
          id: doc.id,
          type: doc.type,
          title: doc.title,
          excerpt: doc.excerpt,
          relevance: doc.relevanceScore || 0.8,
          url: doc.url
        })),
        suggestedQueries: this.generateSuggestedQueries(documents)
      }
    } catch (error) {
      console.error('Erro na chamada da API de IA:', error)
      throw error
    }
  },
  
  // Gerar queries sugeridas
  generateSuggestedQueries(documents: SearchDocument[]): string[] {
    // Lógica simples para gerar queries sugeridas
    const suggestions = [
      'Como funciona o visto de trabalho para brasileiros?',
      'Quais são os melhores bairros para brasileiros em Miami?',
      'Como encontrar emprego nos EUA como brasileiro?',
      'Preços de imóveis nas principais cidades dos EUA',
      'Comunidades de brasileiros no exterior'
    ]
    
    // Adicionar sugestões baseadas no tipo de documentos encontrados
    const types = new Set(documents.map(d => d.type))
    if (types.has('listing')) {
      suggestions.push('Como anunciar um imóvel no Noé Connect?')
    }
    if (types.has('news')) {
      suggestions.push('Últimas notícias para brasileiros nos EUA')
    }
    
    return suggestions.slice(0, 3)
  }
}

export default {
  aiConfigService,
  aiSearchService
}